import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './auth';
import { usePouleLeden, usePoules } from './queries';
import type { Poule } from './types';

/* ==========================================================================
   Welke poule je bekijkt.

   Alles wat met spelen te maken heeft — uitslagen, ranglijst, statistieken,
   toernooien — hoort bij één poule. Die keuze staat hier, wordt onthouden in
   de browser, en valt terug op je favoriet.
   ========================================================================== */

const OPSLAG = 'foosrankr-poule';

interface PouleWaarde {
  /** De poule die je nu bekijkt; null als je nergens in zit. */
  pouleId: string | null;
  poule: Poule | null;
  mijnPoules: Poule[];
  /** Ben je beheerder van de poule die je bekijkt? */
  isPouleBeheerder: boolean;
  favorieten: Set<string>;
  kies: (id: string) => void;
  laden: boolean;
}

const PouleContext = createContext<PouleWaarde | null>(null);

export function PouleProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const poules = usePoules();
  const leden = usePouleLeden();
  const [gekozen, setGekozen] = useState<string | null>(() => localStorage.getItem(OPSLAG));

  const mijnLidmaatschappen = useMemo(
    () => (leden.data ?? []).filter((l) => l.profielId === profile?.id),
    [leden.data, profile?.id]
  );

  const mijnPoules = useMemo(() => {
    const favoriet = new Set(mijnLidmaatschappen.filter((l) => l.favoriet).map((l) => l.pouleId));
    return (poules.data ?? [])
      .filter((p) => mijnLidmaatschappen.some((l) => l.pouleId === p.id))
      .sort((a, b) => {
        /* favorieten eerst, daarna op naam */
        const fa = favoriet.has(a.id) ? 0 : 1;
        const fb = favoriet.has(b.id) ? 0 : 1;
        return fa - fb || a.naam.localeCompare(b.naam);
      });
  }, [poules.data, mijnLidmaatschappen]);

  /* Onthouden keuze bestaat misschien niet meer; val dan terug op de eerste,
     en dat is dankzij de sortering je favoriet. */
  const pouleId = useMemo(() => {
    if (gekozen && mijnPoules.some((p) => p.id === gekozen)) return gekozen;
    return mijnPoules[0]?.id ?? null;
  }, [gekozen, mijnPoules]);

  useEffect(() => {
    if (pouleId) localStorage.setItem(OPSLAG, pouleId);
  }, [pouleId]);

  const waarde = useMemo<PouleWaarde>(
    () => ({
      pouleId,
      poule: mijnPoules.find((p) => p.id === pouleId) ?? null,
      mijnPoules,
      isPouleBeheerder: mijnLidmaatschappen.some((l) => l.pouleId === pouleId && l.rol === 'beheerder'),
      favorieten: new Set(mijnLidmaatschappen.filter((l) => l.favoriet).map((l) => l.pouleId)),
      kies: setGekozen,
      laden: poules.isLoading || leden.isLoading
    }),
    [pouleId, mijnPoules, mijnLidmaatschappen, poules.isLoading, leden.isLoading]
  );

  return <PouleContext.Provider value={waarde}>{children}</PouleContext.Provider>;
}

export function usePoule(): PouleWaarde {
  const waarde = useContext(PouleContext);
  if (!waarde) throw new Error('usePoule moet binnen een PouleProvider staan.');
  return waarde;
}
